import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type HospitalSummary, type HospitalEncounter, type HospitalBill } from '../api';

/**
 * The patient's hospital record: OPD visits and ward admissions, today's live
 * queue tokens, and the bills the hospital chose to share.
 *
 * All four endpoints have been on the server with nothing on the web calling
 * them, so a patient with an admission in progress and an unpaid bill saw no
 * sign of either here. A bill appears only once the hospital shares it — a
 * draft at the counter is not shown, because a figure that is about to change
 * is worse than none.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function fmtDateTime(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

function pretty(s: string): string {
  return (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function statusColor(status: string, active: boolean): string {
  const s = (status || '').toUpperCase();
  if (s.includes('CANCEL')) return T.danger;
  if (s.includes('DISCHARG') || s.includes('COMPLETE')) return T.success;
  return active ? T.teal : T.textSec;
}

function EncounterCard({ e }: { e: HospitalEncounter }) {
  return (
    <div style={{ ...card, borderColor: e.active ? `${T.teal}45` : T.border }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>
            {e.departmentName || e.reasonForVisit || `${e.kind} visit`}
          </div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
            {[e.hospitalName, e.attendingDoctorName].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span style={{
          fontSize: 9.5, fontWeight: 700, color: T.white, whiteSpace: 'nowrap',
          background: statusColor(e.status, e.active), borderRadius: 8, padding: '3px 8px',
        }}>
          {e.kind} · {pretty(e.status)}
        </span>
      </div>

      {(e.ward || e.roomNumber || e.tokenNumber != null) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {e.tokenNumber != null && (
            <span style={{
              fontSize: 11, fontWeight: 800, color: T.teal, background: `${T.teal}12`,
              border: `1px solid ${T.teal}35`, borderRadius: 8, padding: '4px 10px',
            }}>
              Token {e.tokenNumber}
            </span>
          )}
          {e.ward && (
            <span style={{ fontSize: 11, color: T.textSec, background: T.ice, borderRadius: 8, padding: '4px 10px' }}>
              Ward {e.ward}{e.roomNumber ? ` · Room ${e.roomNumber}` : ''}
            </span>
          )}
        </div>
      )}

      {e.diagnosis && (
        <div style={{ fontSize: 11.5, color: T.text, marginTop: 10, lineHeight: 1.5 }}>
          <span style={{ color: T.textSec }}>Diagnosis: </span>{e.diagnosis}
        </div>
      )}

      <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 10 }}>
        {e.admittedAt
          ? `Admitted ${fmtDateTime(e.admittedAt)}${e.dischargedAt ? ` · Discharged ${fmtDateTime(e.dischargedAt)}` : ''}`
          : e.scheduledAt ? `Scheduled ${fmtDateTime(e.scheduledAt)}` : ''}
      </div>

      {e.timeline?.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {e.timeline.map((t) => (
            <div key={`${t.status}-${t.at}`} style={{ fontSize: 10.5, color: T.textSec }}>
              {t.status}
              <span style={{ color: T.textTer }}> — {fmtDateTime(t.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BillCard({ b }: { b: HospitalBill }) {
  const owed = Number(b.patientPayable || 0);
  return (
    <div style={{ ...card, borderColor: owed > 0 ? `${T.warning}55` : T.border }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{b.billNumber}</div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
            {[b.hospitalName, b.invoiceNumber].filter(Boolean).join(' · ')}
          </div>
        </div>
        <span style={{
          fontSize: 9.5, fontWeight: 700, color: T.white, whiteSpace: 'nowrap',
          background: owed > 0 ? T.warning : T.success, borderRadius: 8, padding: '3px 8px',
        }}>
          {pretty(b.paymentStatus)}
        </span>
      </div>

      {b.lineItems?.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {b.lineItems.map((li, i) => (
            <div key={`${li.description}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5 }}>
              <span style={{ color: T.textSec }}>
                {li.description}{li.quantity > 1 ? ` × ${li.quantity}` : ''}
              </span>
              <span style={{ color: T.text, fontWeight: 600 }}>{money(li.amount)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800 }}>
          <span style={{ color: T.text }}>Total</span>
          <span style={{ color: T.text }}>{money(b.netAmount)}</span>
        </div>
        {b.paidAmount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
            <span style={{ color: T.textSec }}>Paid</span>
            <span style={{ color: T.success }}>{money(b.paidAmount)}</span>
          </div>
        )}
        {owed > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800 }}>
            <span style={{ color: T.danger }}>You owe</span>
            <span style={{ color: T.danger }}>{money(owed)}</span>
          </div>
        )}
        {(b.insuranceStatus || b.claimStatus) && (
          <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>
            Insurance: {pretty(b.insuranceStatus || b.claimStatus || '')}
          </div>
        )}
      </div>

      {owed > 0 && (
        <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 10, lineHeight: 1.5 }}>
          Hospital bills are settled at the hospital desk. NeoHealthCard does not collect this payment.
        </div>
      )}
    </div>
  );
}

export default function HospitalRecord() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<HospitalSummary | null>(null);
  const [encounters, setEncounters] = useState<HospitalEncounter[]>([]);
  const [queue, setQueue] = useState<HospitalEncounter[]>([]);
  const [bills, setBills] = useState<HospitalBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, e, q, b] = await Promise.allSettled([
      patientApi.hospital.summary(),
      patientApi.hospital.encounters(),
      patientApi.hospital.queue(),
      patientApi.hospital.bills(),
    ]);
    const bad: string[] = [];
    if (s.status === 'fulfilled') setSummary(s.value); else bad.push('summary');
    if (e.status === 'fulfilled') setEncounters(e.value ?? []); else bad.push('visits');
    if (q.status === 'fulfilled') setQueue(q.value ?? []); else bad.push('queue');
    if (b.status === 'fulfilled') setBills(b.value ?? []); else bad.push('bills');
    setFailed(bad);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const active = encounters.filter((e) => e.active);
  const past = encounters.filter((e) => !e.active);

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>Hospital record</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          Your visits and admissions, today's queue tokens, and the bills a hospital has shared with you.
        </p>
      </div>

      {failed.length > 0 && (
        <div style={{
          ...card, borderColor: `${T.warning}55`, padding: 12, fontSize: 11,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
        }}>
          <span style={{ color: T.text }}>Could not load: {failed.join(', ')}. Everything else is shown.</span>
          <button
            onClick={() => void load()}
            style={{
              fontSize: 10.5, fontWeight: 700, color: T.white, background: T.warning,
              border: 'none', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontFamily: T.font,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading your hospital record…</div>
      ) : summary && !summary.linked ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🏥</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No hospital has your card on file</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            Show your NeoHealthCard at the hospital desk. Your visits, queue tokens and bills appear here once
            a hospital links them to your card.
          </div>
          <button
            onClick={() => navigate('/patient/care-services/hospitals')}
            style={{
              marginTop: 14, padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, color: T.white, background: T.teal, fontFamily: T.font,
            }}
          >
            Find a hospital
          </button>
        </div>
      ) : (
        <>
          {/* Find hospitals & book — the app carries this on its Hospitals
              screen, and the record had no way back to the directory once a
              hospital had linked the patient. */}
          <button
            onClick={() => navigate('/patient/care-services/hospitals')}
            style={{
              width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: T.font,
              background: `${T.teal}0F`, border: `1px solid ${T.teal}33`, borderRadius: 14,
              padding: 14, display: 'flex', alignItems: 'center', gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>Find hospitals &amp; book</div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                Departments · doctors · services · price transparency
              </div>
            </div>
            <span style={{ fontSize: 16, color: T.teal }}>›</span>
          </button>

          {summary && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { label: 'Active visits', value: summary.activeEncounters, color: T.teal },
                { label: 'Live tokens today', value: summary.liveTokens, color: T.info },
                { label: 'Unpaid bills', value: summary.unpaidBills, color: summary.unpaidBills > 0 ? T.warning : T.success },
              ].map((s) => (
                <div key={s.label} style={{ ...card, flex: 1, minWidth: 140, padding: 14, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {queue.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.font, marginTop: 4 }}>
                In the queue today
              </div>
              {queue.map((e) => <EncounterCard key={`q-${e.encounterId}`} e={e} />)}
            </>
          )}

          {active.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.font, marginTop: 4 }}>
                Current visits and admissions
              </div>
              {active.map((e) => <EncounterCard key={e.encounterId} e={e} />)}
            </>
          )}

          {bills.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.font, marginTop: 4 }}>
                Bills shared with you
              </div>
              {bills.map((b) => <BillCard key={b.billId} b={b} />)}
            </>
          )}

          {past.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.font, marginTop: 4 }}>
                Earlier visits
              </div>
              {past.map((e) => <EncounterCard key={e.encounterId} e={e} />)}
            </>
          )}

          {encounters.length === 0 && bills.length === 0 && (
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🏥</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Nothing from a hospital yet</div>
              <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
                A visit, an admission or a bill appears here once a hospital records it against your card.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
